/**
 * The builder store — orchestrates the two-step create over the verified builder
 * endpoints: `POST /companions` (draft) → the describe build (`/:id/build`) OR the
 * upload ingest (`/:id/upload`, which moderates the concept image server-side).
 * Holds no consistency logic (pinned seed + reference are the server's job) and no
 * validation beyond compiling the form (that is `builder-model`); it sequences the
 * calls and surfaces the first `err` (over-quota `402`, persona/upload rejected).
 */
import { type ApiError, isErr, type Result } from '@4f/shared';
import { createSignal } from 'solid-js';
import type { ApiClient } from '../api/client.ts';
import type { CompanionView } from '../api/types.ts';
import {
  type BuilderForm,
  type BuilderMode,
  buildAppearance,
  buildPersona,
} from '../features/builder/builder-model.ts';

/** One builder submission — the mode, the compiled form, and (upload only) the concept image. */
export interface BuilderSubmitInput {
  readonly mode: BuilderMode;
  readonly form: BuilderForm;
  /** Required for the `upload` path; ignored otherwise. */
  readonly file?: File;
}

export interface BuilderStore {
  readonly submitting: () => boolean;
  readonly error: () => ApiError | null;
  /**
   * Create + build. Returns the finished companion on `ok`, the first `err`
   * otherwise, or `null` when the input is structurally invalid (the form guards
   * this before calling — a `null` means "nothing was sent").
   */
  submit(input: BuilderSubmitInput): Promise<Result<CompanionView, ApiError> | null>;
  reset(): void;
}

export function createBuilderStore(api: ApiClient): BuilderStore {
  const [submitting, setSubmitting] = createSignal(false);
  const [error, setError] = createSignal<ApiError | null>(null);

  const submit = async (
    input: BuilderSubmitInput,
  ): Promise<Result<CompanionView, ApiError> | null> => {
    const persona = buildPersona(input.form);
    const appearance = buildAppearance(input.form);
    if (!persona || !appearance) {
      return null;
    }
    if (input.mode === 'upload' && !input.file) {
      return null;
    }

    setSubmitting(true);
    setError(null);
    try {
      const created = await api.createCompanion({
        buildSource: input.mode,
        persona,
        appearance,
      });
      if (isErr(created)) {
        setError(created.error);
        return created;
      }
      const girlfriendId = created.value.id;
      const built =
        input.mode === 'upload' && input.file
          ? await api.uploadConcept(girlfriendId, input.file)
          : await api.buildCompanion(girlfriendId);
      if (isErr(built)) {
        setError(built.error);
      }
      return built;
    } finally {
      setSubmitting(false);
    }
  };

  const reset = (): void => {
    setSubmitting(false);
    setError(null);
  };

  return { submitting, error, submit, reset };
}
